import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationPopup } from "./NotificationPopup";
import { AnnouncementPopup } from "./AnnouncementPopup";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { 
  Home, 
  TrendingUp, 
  Users, 
  MessageCircle, 
  FileText, 
  CreditCard, 
  Settings, 
  Send, 
  User, 
  LogOut, 
  ChevronDown,
  Menu,
  X,
  Shield,
  UserCog,
  BarChart3,
  CheckSquare,
  Bell,
  Megaphone,
  Stethoscope,
  ClipboardList,
  Calendar,
  QrCode
} from "lucide-react";

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
  const [smsCredits, setSmsCredits] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications(api);

  const isSuperadmin = user?.role === "superadmin";
  const isClinicAdmin = user?.role === "clinic_admin";
  const isAdmin = isSuperadmin || isClinicAdmin;

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      loadSmsCredits();
    }
  }, [user]);

  const loadSmsCredits = async () => {
    try {
      const data = await api.get("/sms/credits");
      setSmsCredits(data.sms_credits || 0);
    } catch (error) {
      console.error("Failed to load SMS credits:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: Home,
      show: true
    },
    {
      path: "/checklist",
      label: "Today's Checklist",
      icon: CheckSquare,
      show: isAdmin || user?.role === 'nurse'
    },
    {
      path: "/admin-dashboard",
      label: "Admin Dashboard",
      icon: TrendingUp,
      show: isAdmin
    },
    {
      label: "Patients",
      icon: Users,
      show: isAdmin || user?.role === 'nurse',
      subItems: [
        {
          path: "/patients",
          label: "All Patients",
          icon: Users,
          show: isAdmin || user?.role === 'nurse'
        },
        {
          path: "/appointments",
          label: "Appointments",
          icon: Calendar,
          show: true
        },
        {
          path: "/qr-scanner",
          label: "QR Scanner",
          icon: QrCode,
          show: isAdmin || user?.role === 'nurse'
        },
        {
          path: "/calendar",
          label: "Calendar View",
          icon: Calendar,
          show: true
        },
        {
          path: "/further-checkups",
          label: "Further Checkups",
          icon: ClipboardList,
          show: isAdmin || user?.role === 'nurse'
        }
      ]
    },
    {
      path: "/doctors",
      label: "Doctor Management",
      icon: Stethoscope,
      show: isAdmin
    },
    {
      label: "SMS",
      icon: MessageCircle,
      show: true,
      subItems: [
        {
          path: "/marketing-sms",
          label: "Marketing SMS",
          icon: Send,
          show: !isSuperadmin
        },
        {
          path: "/sms-logs",
          label: "SMS Logs",
          icon: FileText,
          show: true
        },
        {
          path: "/sms-credit-allocation",
          label: "SMS Credits",
          icon: CreditCard,
          show: isSuperadmin
        },
        {
          path: "/admin-bulk-sms",
          label: "Bulk SMS to Admins",
          icon: Send,
          show: isSuperadmin
        }
      ]
    },
    {
      label: "Settings",
      icon: Settings,
      show: true,
      subItems: [
        {
          path: "/settings",
          label: "Settings",
          icon: Settings,
          show: isSuperadmin
        },
        {
          path: "/checklist/settings",
          label: "Clinic Settings",
          icon: Bell,
          show: isAdmin && !isSuperadmin
        },
        {
          path: "/user-management",
          label: "User Management",
          icon: UserCog,
          show: isAdmin
        },
        {
          path: "/announcements",
          label: "Announcements",
          icon: Megaphone,
          show: isAdmin
        },
        {
          path: "/audit-logs",
          label: "Audit Logs",
          icon: Shield,
          show: isAdmin
        },
        {
          path: "/system-analytics",
          label: "System Analytics",
          icon: BarChart3,
          show: isSuperadmin
        }
      ]
    },
    {
      path: "/help-support",
      label: "Help & System Support",
      icon: Bell,
      show: true
    }
  ].filter(item => item.show);

  const toggleDesktopSidebar = useCallback(() => {
    setDesktopSidebarCollapsed(prev => !prev);
  }, []);

  const NavItem = ({ item, onClick, collapsed }) => {
    const Icon = item.icon;
    const active = item.path && isActive(item.path);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    
    // Filter visible subitems
    const visibleSubItems = hasSubItems 
      ? item.subItems.filter(subItem => subItem.show) 
      : [];

    // Parent item with subitems - always show subitems (no toggle)
    if (hasSubItems) {
      return (
        <div>
          <div
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
            style={{
              color: '#555555',
              pointerEvents: 'auto',
              cursor: 'default'
            }}
            title={collapsed ? item.label : ''}
          >
            <Icon size={20} />
            {!collapsed && (
              <span className="text-sm flex-1 text-left font-medium">{item.label}</span>
            )}
          </div>
          
          {/* Submenu items - always visible */}
          {!collapsed && visibleSubItems.length > 0 && (
            <div className="mt-1 space-y-1">
              {visibleSubItems.map((subItem) => {
                const SubIcon = subItem.icon;
                const subActive = isActive(subItem.path);
                
                return (
                  <button
                    key={subItem.path}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(subItem.path);
                      if (onClick) onClick();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-xl transition-all cursor-pointer ${
                      subActive
                        ? 'font-semibold' 
                        : 'hover:bg-purple-50'
                    }`}
                    style={{
                      backgroundColor: subActive ? '#f3e8ff' : 'transparent',
                      color: subActive ? '#652D90' : '#555555',
                      borderLeft: subActive ? '4px solid #652D90' : '4px solid transparent',
                      paddingLeft: '2.5rem',
                      pointerEvents: 'auto'
                    }}
                  >
                    <SubIcon size={18} />
                    <span className="text-sm text-left">{subItem.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular navigation item without subitems
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(item.path);
          if (onClick) onClick();
        }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer ${
          active 
            ? 'font-semibold' 
            : 'hover:bg-purple-50'
        }`}
        style={{
          backgroundColor: active ? '#f3e8ff' : 'transparent',
          color: active ? '#652D90' : '#555555',
          borderLeft: active ? '4px solid #652D90' : '4px solid transparent',
          pointerEvents: 'auto'
        }}
        title={collapsed ? item.label : ''}
      >
        <Icon size={20} />
        {!collapsed && <span className="text-sm text-left flex-1">{item.label}</span>}
      </button>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#fcfbf9", fontFamily: "Poppins, sans-serif" }}>
      {/* Top Navigation Bar */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 border-b" 
        style={{ 
          backgroundColor: "rgba(255,255,255,0.95)", 
          backdropFilter: "blur(12px)", 
          borderColor: "#e5e5e5" 
        }}
      >
        <div className="flex items-center justify-between px-6 py-4">
          {/* Left: Logo and Mobile Menu Button */}
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-purple-50 transition-colors"
              style={{ color: "#652D90" }}
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            {/* Desktop Collapse Button */}
            <button
              onClick={toggleDesktopSidebar}
              className="hidden lg:block p-2 rounded-lg hover:bg-purple-50 transition-colors"
              style={{ color: "#652D90" }}
              title={desktopSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Menu size={24} />
            </button>
            
            <h1 
              className="text-xl lg:text-2xl font-bold cursor-pointer" 
              style={{ color: "#652D90" }}
              onClick={() => navigate("/dashboard")}
            >
              Health Yaad Aayo
            </h1>
          </div>

          {/* Right: Notifications, SMS Credits, Profile, Logout */}
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 rounded-full hover:bg-purple-50 transition-colors"
              style={{ color: "#652D90" }}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* SMS Credits Badge */}
            {user?.role !== 'superadmin' && smsCredits !== null && (
              <div 
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold" 
                style={{ backgroundColor: "#f3e8ff", border: "1px solid #652D90", color: "#652D90" }}
              >
                <CreditCard size={16} />
                <span className="hidden md:inline">SMS Credits:</span>
                <span>{smsCredits}</span>
              </div>
            )}

            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full" size="sm">
                  <User size={16} className="mr-2" />
                  <span className="hidden sm:inline">Profile</span>
                  <ChevronDown size={14} className="ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium" style={{ color: "#652D90" }}>
                    {user?.full_name || user?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  <p className="text-xs mt-1 font-medium" style={{ color: "#888" }}>
                    {user?.role === "superadmin" ? "Superadmin" : user?.role === "clinic_admin" ? "Clinic Admin" : user?.role === "nurse" ? "Nurse" : "Doctor"}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} disabled={!isSuperadmin}>
                  <Settings size={16} className="mr-2" />
                  Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Logout Button */}
            <Button 
              onClick={handleLogout}
              variant="outline" 
              size="sm"
              className="rounded-full"
              style={{ borderColor: "#652D90", color: "#652D90" }}
            >
              <LogOut size={16} className="mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Left Sidebar - Desktop */}
      <aside
        className={`hidden lg:block fixed left-0 top-[73px] bottom-0 border-r overflow-y-auto transition-all duration-300 ${
          desktopSidebarCollapsed ? 'w-20' : 'w-64'
        }`}
        style={{ 
          backgroundColor: "#ffffff", 
          borderColor: "#e5e5e5",
          zIndex: 30, // Reduced z-index to avoid conflicts
          pointerEvents: 'auto' // Ensure sidebar can receive clicks
        }}
      >
        <nav className="p-4 space-y-1" style={{ pointerEvents: 'auto' }}>
          {navItems.map((item, index) => (
            <NavItem key={item.path || item.label || index} item={item} collapsed={desktopSidebarCollapsed} />
          ))}
        </nav>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
          style={{ top: "73px" }}
        />
      )}

      {/* Left Sidebar - Mobile */}
      <aside
        className={`lg:hidden fixed left-0 top-[73px] bottom-0 w-64 border-r overflow-y-auto transition-transform duration-300 z-50 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ 
          backgroundColor: "#ffffff", 
          borderColor: "#e5e5e5"
        }}
      >
        <nav className="p-4 space-y-1">
          {navItems.map((item, index) => (
            <NavItem 
              key={item.path || item.label || index} 
              item={item} 
              onClick={() => setSidebarOpen(false)}
            />
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main 
        className={`pt-[73px] transition-all duration-300 ${
          desktopSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        {children}
      </main>

      {/* Notification Popup */}
      {showNotifications && (
        <NotificationPopup
          notifications={notifications}
          onMarkAsRead={markAsRead}
          onDelete={deleteNotification}
          onClose={() => setShowNotifications(false)}
        />
      )}
      
      {/* Announcement Popup */}
      <AnnouncementPopup />
    </div>
  );
};
